import React, { useEffect, useState, useMemo } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import LogoutIcon from "@mui/icons-material/Logout";
import BackupIcon from "@mui/icons-material/Backup";
 import NavigateNextIcon from "@mui/icons-material/NavigateNext";
  import BoltIcon from '@mui/icons-material/Bolt';
  import LockClockIcon from '@mui/icons-material/LockClock';
  import QuizIcon from '@mui/icons-material/Quiz';
  import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip, Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Select,
  FormControl,
  InputLabel,
  DialogContentText,
  Modal,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar"; // Add Snackbar import
import Alert from "@mui/material/Alert"; // Add Alert import
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DeleteIcon from "@mui/icons-material/Delete";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import StarIcon from "@mui/icons-material/Star";
import FolderIcon from "@mui/icons-material/Folder";
import SubfolderIcon from "@mui/icons-material/FolderSpecial";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TreeView from "./TreeView";
import flash from "./assets/thunder.png";
import notes from "./assets/note.png";
import { Link } from "react-router-dom";
import { DarkMode, LiveHelp } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import axios from 'axios';
import { useContext } from 'react';
// import { ThemeContext } from '../context/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { ThemeContext } from '../context/ThemeContext';
import PaletteIcon from '@mui/icons-material/Palette';
import ThemeToggle from "./ThemeToggle";
import CustomThemePicker from "./CustomThemePicker";

const initialDrawerWidth = 325;
const minDrawerWidth = 100;
const maxDrawerWidth = 600;

const openedMixin = (theme, drawerWidth) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});


const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open, drawerWidth }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open, drawerWidth }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme, drawerWidth),
    "& .MuiDrawer-paper": openedMixin(theme, drawerWidth),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const ItemTypes = {
  ITEM: "item",
};

const DraggableListItem = ({ item, index, moveItem, setEditingIndex }) => {
  const ref = React.useRef(null);
  const [, drop] = useDrop({
    accept: ItemTypes.ITEM,
    hover(draggedItem) {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ITEM,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));


  return (
    <ListItem
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      disablePadding
      sx={{ display: "block", pl: 4 }}
    >
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: "initial",
          px: 2.5,
          position: "relative",
          "&:hover .edit-delete-buttons": {
            display: "flex",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: 3,
            justifyContent: "center",
            color: "inherit",
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} sx={{ color: "inherit" }} />
        <Box
          className="edit-delete-buttons"
          sx={{
            display: "none",
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            gap: 1,
          }}
        >
          <IconButton onClick={() => setEditingIndex(index)} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

export default function MiniDrawer({ content }) {
const getPageNameFromPath = (pathname) => {
  if (pathname === "/home") return "Home Page";
  if (pathname === "/flashpractice") return "FlashCard Practice";
  if (pathname === "/allquiz") return "Quizzes Practice";
  if (pathname === "/control-panel") return "Control Panel";
  if (pathname.startsWith("/cards/")) return null; // Return null for folder routes
  return ""; // Return empty string for unknown routes (will trigger update)
};

  
const [currentPage, setCurrentPage] = useState(() => {
  const pathname = window.location.pathname;
  return getPageNameFromPath(pathname);
});

const [exportTemplateFormat, setExportTemplateFormat] = useState("csv"); // Default to CSV
const [importFormat, setImportFormat] = useState("csv"); // Default to CSV
const [exportFormat, setExportFormat] = useState("csv"); // Default to CSV
const [activePage, setActivePage] = useState(() => {
  const pathname = window.location.pathname;
  return getPageNameFromPath(pathname);
});  const [folders, setFolders] = useState([]);
const [selectedFolders, setSelectedFolders] = useState([]);
const [selectedFoldersForExport, setSelectedFoldersForExport] = useState([]);
const [customExportFilename, setCustomExportFilename] = useState(''); // ✅ Add here
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [drawerWidth, setDrawerWidth] = React.useState(initialDrawerWidth);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [createAnchorEl, setCreateAnchorEl] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [newItemType, setNewItemType] = React.useState(null);
  const [newItemName, setNewItemName] = React.useState("");
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [subfolderName, setSubfolderName] = React.useState("");
  const [parentFolderName, setParentFolderName] = React.useState("");
  const [parentFolderId, setParentFolderId] = React.useState(null); // Store parent folder ID
  const { subfolder_id } = useParams();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [sessionDetails, setSessionDetails] = useState(null); // State for session data
  const [activityModalOpen, setActivityModalOpen] = useState(false); // Modal state
  const [profileId, setProfileId] = useState(null); // To store the profile ID after creation
  const [lastSavedProfileData, setLastSavedProfileData] = useState(null); // store last saved profile data
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false); // For cancel confirmation dialog
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if changes were made
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [profileCreated, setProfileCreated] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  // const { darkMode, setDarkMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  const [openThemeModal, setOpenThemeModal] = useState(false);
  const [requiredOpen, setRequiredOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [permissions, setPermissions] = useState({
    can_import_folder: true,
    can_export_folder: true,
    can_export_template: true, // Add this
    can_create_profile: true,
    can_read_profile: true,
    can_update_profile: true,
  });
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  // state for error modal
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: "",
  });

  const getIcon = (type) => {
    switch (type) {
      case "Folder":
        return <FolderIcon color="secondary" />;
      case "Subfolder":
        return <SubfolderIcon color="action" />;

      default:
        return null;
    }
  };
  // Handler to show error modal
  const handleErrorModalOpen = (message) => {
    setErrorModal({ open: true, message });
  };

  // Handler to close error modal
  const handleErrorModalClose = () => {
    setErrorModal({ open: false, message: "" });
  };


  // Open dialog
  const handleRequiredOpen = (message) => {

    setRequiredOpen(true);
  };

  // Close dialog
  const handleRequiredClose = () => {
    setRequiredOpen(false);

  };


  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/user/permissions/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUserRole(data.role_name);
      if (data.role_name === "admin") {
        setPermissions({
          can_import_folder: true,
          can_export_folder: true,
          can_export_template: true, // Added this
          can_create_profile: false,
          can_read_profile: false,
          can_update_profile: false,
        });
      } else {
        setPermissions({
          can_import_folder: data.permissions.can_import_folder,
          can_export_folder: data.permissions.can_export_folder,
          can_export_template: data.permissions.can_export_template, // Added this
          can_create_profile: data.permissions.can_create_profile,
          can_read_profile: data.permissions.can_read_profile,
          can_update_profile: data.permissions.can_update_profile,
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch permissions. Please try again.",
        severity: "error",
      });
    }
  };

useEffect(() => {
  const fetchFolderName = async () => {
    const currentPath = window.location.pathname;

    if (currentPath.startsWith("/cards/")) {
      const folderId = currentPath.split("/cards/")[1];

      if (folderId) {
        try {
          const token = sessionStorage.getItem("authToken");
          const response = await fetch(`${apiUrl}/folders/`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();

            // Combine all folders (own, free, paid)
            const allFolders = [
              ...(data.own_folders || []),
              ...(data.assigned_folders?.free || []),
              ...(data.assigned_folders?.paid || []),
            ];

            // Recursively find the folder by ID
            const findById = (folders, targetId) => {
              for (const folder of folders) {
                if (folder.id === parseInt(targetId)) return folder;
                if (folder.subfolders && folder.subfolders.length > 0) {
                  const found = findById(folder.subfolders, targetId);
                  if (found) return found;
                }
              }
              return null;
            };

            const folder = findById(allFolders, folderId);

            if (folder) {
              // Build parent chain for expansion
              const buildParentChain = (targetId) => {
                const parents = [];
                let currentId = targetId;

                while (currentId !== null && currentId !== undefined) {
                  const current = findById(allFolders, currentId);
                  if (!current || current.parent === null || current.parent === undefined) break;
                  parents.unshift(current.parent);
                  currentId = current.parent;
                }

                return parents;
              };

              const parents = buildParentChain(folder.id);
              setExpandedFolders(parents);

              // If it's a subfolder, show parent > subfolder
              if (folder.parent !== null && folder.parent !== undefined) {
                const parent = findById(allFolders, folder.parent);
                if (parent) {
                  setCurrentPage(`${parent.name} > ${folder.name}`);
                  setParentFolderName(parent.name);
                  setParentFolderId(parent.id);
                } else {
                  setCurrentPage(folder.name);
                  setParentFolderName("");
                  setParentFolderId(null);
                }
              } else {
                setCurrentPage(folder.name);
                setParentFolderName("");
                setParentFolderId(null);
              }
              setActivePage("Folder View");
              setSelectedFolderId(parseInt(folderId));
            } else {
              setCurrentPage("Folder View");
            }
          }
        } catch (error) {
          console.error("Error fetching folder name:", error);
          setCurrentPage("Folder View");
        }
      }
    }
  };

  fetchFolderName();
}, []);


// Add this effect to handle immediate title updates
// Add this effect to handle immediate title updates
useEffect(() => {
  window.updatePageTitle = (node) => {
    if (node.parent !== null && node.parent !== undefined) {
      const token = sessionStorage.getItem("authToken");
      fetch(`${apiUrl}/folders/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          // Combine all folders (own, free, paid)
          const allFolders = [
            ...(data.own_folders || []),
            ...(data.assigned_folders?.free || []),
            ...(data.assigned_folders?.paid || []),
          ];

          // Recursively find the parent by ID
          const findById = (folders, targetId) => {
            for (const folder of folders) {
              if (folder.id === targetId) return folder;
              if (folder.subfolders && folder.subfolders.length > 0) {
                const found = findById(folder.subfolders, targetId);
                if (found) return found;
              }
            }
            return null;
          };

          const parent = findById(allFolders, node.parent);

          if (parent) {
            setCurrentPage(`${parent.name} > ${node.name}`);
            setParentFolderName(parent.name);
            setParentFolderId(parent.id);
          } else {
            setCurrentPage(node.name);
            setParentFolderName("");
            setParentFolderId(null);
          }
        })
        .catch((error) => {
          console.error("Error in updatePageTitle:", error);
          setCurrentPage(node.name);
        });
    } else {
      setCurrentPage(node.name);
      setParentFolderName("");
      setParentFolderId(null);
    }
  };

  return () => {
    delete window.updatePageTitle;
  };
}, [apiUrl]);

const findFolderById = (folders, id) => {
  for (const folder of folders) {
    if (folder.id === parseInt(id)) return folder;
    if (folder.subfolders?.length) {
      const found = findFolderById(folder.subfolders, id);
      if (found) return found;
    }
  }
  return null;
};


useEffect(() => {
  const fetchFolders = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        setSnackbar({
          open: true,
          message: "No authentication token found. Please log in again.",
          severity: "error",
        });
        return;
      }

      const response = await fetch(`${apiUrl}/folders/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: errorData.detail || `Failed to fetch folders: HTTP error! status: ${response.status}`,
          severity: "error",
        });
        return;
      }

      const responseData = await response.json();
      console.log("API response data:", responseData);

      // ✅ FIX: Access the 'own_folders' array from the response
     const foldersArray = [
  ...(responseData.own_folders || []),
  ...(responseData.assigned_folders?.free || []),
  ...(responseData.assigned_folders?.paid || []),
];


      if (!Array.isArray(foldersArray)) {
        console.error("Expected an array for own_folders, got:", foldersArray);
        return;
      }

      const findSubfolder = (folders, id) => {
        for (const folder of folders) {
          if (folder.id === parseInt(id)) {
            return folder;
          }
          if (folder.subfolders && Array.isArray(folder.subfolders)) {
            const found = findSubfolder(folder.subfolders, id);
            if (found) return found;
          }
        }
        return null;
      };

     const subfolder = findFolderById(foldersArray, subfolder_id);


      if (!subfolder) {
        console.log("Subfolder not found");
        setSubfolderName("");
        setParentFolderName("");
        setParentFolderId(null);
        setCurrentPage("Subfolder Not Found");
        return;
      }

      setSubfolderName(subfolder.name);

      // Only try to find the parent if subfolder.parent exists and foldersArray is a non-empty array
     let parent = null;
if (subfolder?.parent) {
  parent = findFolderById(foldersArray, subfolder.parent);
}


      if (parent) {
        setParentFolderName(parent.name);
        setParentFolderId(parent.id);
        setCurrentPage(`${parent.name} > ${subfolder.name}`);
      } else {
        setParentFolderName("");
        setParentFolderId(null);
        setCurrentPage(subfolder.name);
      }
      setActivePage("Folder View");
      setSelectedFolderId(parseInt(subfolder_id));
    } catch (error) {
      console.error("Error fetching folders:", error);
      // ✅ FIX: Only show error if we actually have a subfolder_id to fetch
      if (subfolder_id) {
        setSnackbar({
          open: true,
          message: "Failed to fetch folders: Network error or server unavailable.",
          severity: "error",
        });
      }
    }
  };

  // ✅ FIX: Only run if subfolder_id exists
  if (subfolder_id) {
    fetchFolders();
  }
}, [subfolder_id, apiUrl]);

useEffect(() => {
  const fetchFolderName = async () => {
    const currentPath = window.location.pathname;
    
    // Check if we're on a folder/subfolder route
    if (currentPath.startsWith("/cards/")) {
      const folderId = currentPath.split("/cards/")[1];
      
      if (folderId) {
        try {
          const token = sessionStorage.getItem("authToken");
          const response = await fetch(`${apiUrl}/folders/`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            
            // ✅ FIX: Combine ALL folder sources
            const allFolders = [
              ...(data.own_folders || []),
              ...(data.assigned_folders?.free || []),
              ...(data.assigned_folders?.paid || [])
            ];
            
            // Recursive function to find folder and its parents
            const findFolderWithParents = (folders, id, parents = []) => {
              for (const folder of folders) {
                if (folder.id === parseInt(id)) {
                  return { folder, parents };
                }
                if (folder.subfolders && folder.subfolders.length > 0) {
                  const found = findFolderWithParents(folder.subfolders, id, [...parents, folder.id]);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const result = findFolderWithParents(allFolders, folderId);
            
            if (result) {
              const { folder, parents } = result;
              
              // Set expanded folders to include all parents
              setExpandedFolders(parents);
              
              // If it's a subfolder, show parent > subfolder
              if (folder.parent !== null && folder.parent !== undefined) {
                // ✅ FIX: Search recursively for parent in all folders
                const findById = (folders, targetId) => {
                  for (const f of folders) {
                    if (f.id === targetId) return f;
                    if (f.subfolders) {
                      const found = findById(f.subfolders, targetId);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                const parent = findById(allFolders, folder.parent);
                if (parent) {
                  setCurrentPage(`${parent.name} > ${folder.name}`);
                  setParentFolderName(parent.name);
                  setParentFolderId(parent.id);
                } else {
                  setCurrentPage(folder.name);
                  setParentFolderName("");
                  setParentFolderId(null);
                }
              } else {
                setCurrentPage(folder.name);
                setParentFolderName("");
                setParentFolderId(null);
              }
              setActivePage("Folder View");
              setSelectedFolderId(parseInt(folderId));
            }
          }
        } catch (error) {
          console.error("Error fetching folder name:", error);
          setCurrentPage("Folder View");
        }
      }
    }
  };

  fetchFolderName();
}, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      fetchPermissions();
    }
  }, []);

useEffect(() => {
  const fetchFolders = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/folders/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        // ✅ FIX: Access own_folders and filter main folders
        const foldersArray = data.own_folders || [];
        const mainFolders = foldersArray.filter((folder) => folder.parent === null);
        setFolders(mainFolders);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to fetch folders.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Network error or server unavailable.",
        severity: "error",
      });
    }
  };

  if (openDialog) {
    fetchFolders();
  }
}, [openDialog]);


const handleExportTemplate = async () => {
  const token = sessionStorage.getItem("authToken");

  try {
    const endpoint = exportTemplateFormat === "csv"
      ? `${apiUrl}/export-empty-template/`
      : `${apiUrl}/download/markdown-template/`;

    const response = await axios.get(endpoint, {
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    });

    const filename = exportTemplateFormat === "csv"
      ? "flashcard_template.csv"
      : "flashcard_template.md";

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    setSnackbar({
      open: true,
      message: "✅ Template downloaded successfully.",
      severity: "success",
    });
  } catch (error) {
    console.error("Template Export Error:", error);
    setSnackbar({
      open: true,
      message: "❌ Failed to download template: " + (error.response?.data?.message || "Check token or server."),
      severity: "error",
    });
  } finally {
    setOpenTemplateDialog(false);
  }
}; 



  const handleCreateModalOpen = () => {
    setCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    // setProfileModalOpen(false);
    setCreateModalOpen(false);
  };
  const [profileData, setProfileData] = useState({
    admission_number: "",
    date_of_joining: "",
    dob: "",
    fathers_name: "",
    fathers_occupation: "",
    fathers_phone: "",
    mothers_name: "",
    mothers_occupation: "",
    mothers_phone: "",
    emergency_contact_number: "",
    guardian_name: "",
    relationship_with_guardian: "",
    guardian_phone: "",
    permanent_address: "",
    current_address: "",
    blood_group: "",
    email: "",
    alternate_email: "",
    gender: "",
    photo: null,
    student_class: "",
    school: "",
    religion: "",
    caste: "",
    mother_tongue: "",
    physically_challenged: "",
    height: "",
    weight: ""
  });
  const [validationErrors, setValidationErrors] = useState({
    fathers_phone: "",
    mothers_phone: "",
    guardian_phone: "",
    emergency_contact_number: ""
  });

  const [snackbar, setSnackbar] = useState({ // Add Snackbar state
    open: false,
    message: "",
    severity: "success", // 'success' or 'error'
  });

  // Handler to close the Snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handler to restrict input to numbers only
  const handleNumericInput = (e) => {
    const charCode = e.charCode;
    if (charCode < 48 || charCode > 57) { // Allow only numbers (0-9)
      e.preventDefault();
    }
  };

  const normalizePhysicallyChallenged = (profile) => ({
    ...profile,
    physically_challenged: profile.physically_challenged !== null && profile.physically_challenged !== undefined
      ? String(profile.physically_challenged)
      : ""
  });


  // Use useMemo to determine if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!lastSavedProfileData) return true; // If no last saved data, treat current data as unsaved
    return JSON.stringify(profileData) !== JSON.stringify(lastSavedProfileData);
  }, [profileData, lastSavedProfileData]);

  // Update hasUnsavedChanges whenever profileData changes
  useEffect(() => {
    setHasUnsavedChanges(hasChanges);
  }, [hasChanges]);

  // Handler for profile change to ensure validation
  const handleProfileChange = (key, value) => {
    setProfileData((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Validate phone numbers
    if (["fathers_phone", "mothers_phone", "guardian_phone", "emergency_contact_number"].includes(key)) {
      if (value.length !== 10 && value.length > 0) {
        setValidationErrors((prev) => ({
          ...prev,
          [key]: "Phone number must be exactly 10 digits",
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          [key]: "",
        }));
      }
    }
  };

  // Options for select fields
  const genderOptions = ["Male", "Female", "Other"];
  const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const physicallyChallengedOptions = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" }
  ];

  // ✅ Profile Modal Open Handler
  const handleProfileModalOpen = async () => {
    const role = sessionStorage.getItem("userRole");

    if (role === "admin") {
      setSnackbar({
        open: true,
        message: "Admin does not have access to profile.",
        severity: "warning",
      });
      return;
    }

    if (!permissions.can_read_profile) {
      setSnackbar({
        open: true,
        message: "You do not have permission to view profiles.",
        severity: "error",
      });
      return;
    }

    setProfileModalOpen(true);
    await fetchProfile();
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
    setIsProfileEditing(false);
  };

  // Initial state for resetting (defined as a constant for clarity)
  const initialProfileData = {
    admission_number: "",
    date_of_joining: "",
    dob: "",
    fathers_name: "",
    fathers_occupation: "",
    fathers_phone: "",
    mothers_name: "",
    mothers_occupation: "",
    mothers_phone: "",
    emergency_contact_number: "",
    guardian_name: "",
    relationship_with_guardian: "",
    guardian_phone: "",
    permanent_address: "",
    current_address: "",
    blood_group: "",
    email: "",
    alternate_email: "",
    gender: "",
    photo: null,
    student_class: "",
    school: "",
    religion: "",
    caste: "",
    mother_tongue: "",
    physically_challenged: "",
    height: "",
    weight: ""
  };

  // Handler functions handleProfileEditToggle
  const handleProfileEditToggle = () => {
    if (isProfileEditing && hasUnsavedChanges) {
      setCancelConfirmationOpen(true);
    } else {
      setIsProfileEditing(!isProfileEditing);

    }
  };

  // Handler for confirming cancel
  const handleConfirmCancel = () => {
    if (lastSavedProfileData) {
      setProfileData(lastSavedProfileData);
    }
    setIsProfileEditing(false);
    setCancelConfirmationOpen(false);
  };

  // Handler for denying cancel
  const handleDenyCancel = () => {
    setCancelConfirmationOpen(false);
  };

  // Handle photo upload
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData((prev) => ({
          ...prev,
          photo: reader.result, // Store base64 string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Modified fetchProfile with error modal handling
  const fetchProfile = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const role = sessionStorage.getItem("userRole");

      if (!token) {
        handleErrorModalOpen("No authentication token found. Please log in again.");
        return;
      }

      if (role === "admin") {
        console.log("Admin user – skipping profile fetch.");
        return;
      }

      const response = await fetch(`${apiUrl}/profile/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const profile = data[0];
          const updatedProfile = normalizePhysicallyChallenged(profile);

          setProfileData(updatedProfile);
          setLastSavedProfileData(profile);
          setProfileId(profile.id);
          setProfileCreated(true);

          localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
          localStorage.setItem("profileId", profile.id);
        } else {
          console.log("No profile data found (empty array). User can create profile.");
          setProfileCreated(false);
        }
      } else if (response.status === 500) {
        // 🔥 Special handling for API returning 500 when no profile exists
        console.warn("No profile exists yet (server returned 500). Skipping error modal.");
        handleCreateModalOpen();
        setProfileCreated(false);
      } else {
        handleErrorModalOpen(`Failed to fetch profile: HTTP error! status: ${response.status}`);
      }

    } catch (error) {
      console.error("Error fetching profile:", error);
      handleErrorModalOpen("Failed to fetch profile: Network error or server unavailable.");
    }
  };



  // Modified handleProfileSave with error modal handling
  const handleProfileSave = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        setSnackbar({
          open: true,
          message: "No authentication token found. Please log in again.",
          severity: "error",
        });
        return;
      }

      if (!profileCreated && !permissions.can_create_profile) {
        setSnackbar({
          open: true,
          message: "You do not have permission to create a profile.",
          severity: "error",
        });
        return;
      }

      if (profileCreated && !permissions.can_update_profile) {
        setSnackbar({
          open: true,
          message: "You do not have permission to update the profile.",
          severity: "error",
        });
        return;
      }

      // Check for required fields
      const requiredFields = [
        "admission_number",
        "date_of_joining",
        "dob",
        "fathers_name",
        "mothers_name",
        "permanent_address",
        "current_address",
        "email",
        "gender",
        "student_class",
        "school",
      ];
      const missingFields = requiredFields.filter((field) => !profileData[field]);

      if (missingFields.length > 0) {
        setRequiredOpen(true);
        return;
      }

      const url = profileCreated
        ? `${apiUrl}/profile/${profileId}/`
        : `${apiUrl}/profile/`;
      const method = profileCreated ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const responseData = await response.json();
        setIsProfileEditing(false);
        setLastSavedProfileData(profileData);
        if (!profileCreated) {
          setProfileId(responseData.id);
          setProfileCreated(true);
          setSnackbar({
            open: true,
            message: "Profile created successfully!",
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: "Profile updated successfully!",
            severity: "success",
          });
        }
        localStorage.setItem("userProfile", JSON.stringify(profileData));
        localStorage.setItem("profileId", responseData.id || profileId);
      } else {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: `Failed to ${profileCreated ? "update" : "create"} profile: ${errorData.message || "Unknown error"}`,
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
    }
  };

  const navigate = useNavigate(); // Initialize navigate

  // Activity Modal handlers
  const handleActivityModalOpen = () => {
    const storedSessionDetails = sessionStorage.getItem("sessionActivity");
    if (storedSessionDetails) {
      const parsedDetails = JSON.parse(storedSessionDetails);
      console.log("Parsed session details:", parsedDetails);
      setSessionDetails(parsedDetails);
      setActivityModalOpen(true);
    } else {
      setSessionDetails({
        sessionDuration: "N/A",
        loginTime: "N/A",
        logoutTime: "N/A",
      });
      setActivityModalOpen(true);
      setErrorModal({
        open: true,
        message: "No session activity data found.",
      });
    }
  };

  const handleActivityModalClose = () => {
    console.log("Closing activity modal", activityModalOpen);
    setActivityModalOpen(false);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMouseDown = (e) => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const newWidth = e.clientX > minDrawerWidth ? e.clientX : minDrawerWidth;
    setDrawerWidth(Math.min(newWidth, maxDrawerWidth));
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateMenuClick = (event) => {
    setCreateAnchorEl(event.currentTarget);
  };

  const handleCreateMenuClose = (type) => {
    if (type) {
      setNewItemType(type);
    }
    setCreateAnchorEl(null);
  };

  const handleNewItemSubmit = (e) => {
    if (e.key === "Enter" && newItemName) {
      if (isEditing) {
        const updatedItems = [...items];
        updatedItems[editingIndex].text = newItemName;
        setItems(updatedItems);
        setIsEditing(false);
        setEditingIndex(null);
      } else {
        const newItem = { text: newItemName, icon: getIcon(newItemType) };
        setItems((prevItems) => [...prevItems, newItem]);
      }
      setNewItemType(null);
      setNewItemName("");
    }
  };

  const handleDeleteItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const handleEditItem = (index) => {
    setNewItemName(items[index].text);
    setIsEditing(true);
    setEditingIndex(index);
    setNewItemType(items[index].type);
  };

  // 🔽 Import Handler
  const handleImport = async () => {
  const token = sessionStorage.getItem('authToken');
  if (!selectedFile) {
    setSnackbar({
      open: true,
      message: '⚠️ Please select a file.',
      severity: 'warning',
    });
    return;
  }

  const formData = new FormData();
  formData.append('file', selectedFile);

  try {
    const endpoint = importFormat === "csv"
      ? `${apiUrl}/import-questions-csv-with-folders/`
      : `${apiUrl}/import/questions/markdown/`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Server error');
    }

    setSnackbar({
      open: true,
      message: `✅ ${importFormat === "csv" ? "CSV" : "Markdown"} file imported successfully.`,
      severity: 'success',
    });
  } catch (error) {
    console.error('Full import error:', error);
    setSnackbar({
      open: true,
      message: `❌ Import failed: ${error.message}`,
      severity: 'error',
    });
  } finally {
    setImportDialog(false);
    window.location.reload();
  }
};

  const handleImportJSON = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "❌ Please select a JSON file to import.",
        severity: "error",
      });
      return;
    }

    if (!selectedFile.name.endsWith('.json')) {
      setSnackbar({
        open: true,
        message: "❌ Please select a valid JSON file.",
        severity: "error",
      });
      return;
    }

    const token = sessionStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${apiUrl}/import/json/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({
        open: true,
        message: '✅ JSON import successful.',
        severity: 'success',
      });
      setImportDialog(false);
      setSelectedFile(null);
      window.location.reload(); // Refresh the page after successful import
    } catch (error) {
      console.error('Import JSON Error:', error);
      setSnackbar({
        open: true,
        message: '❌ JSON import failed: ' + (error.response?.data?.message || 'Check token or file format.'),
        severity: 'error',
      });
    }
  };


  // 🔽 Export Handler
const handleExport = async () => {
  const token = sessionStorage.getItem("authToken");
  try {
    let url;
    const isSelectAll = selectedFoldersForExport.length === folders.length;

    if (exportFormat === "csv") {
      const folderIds = selectedFoldersForExport.map((folder) => folder.id).join(",");
      url = `${apiUrl}/export/questions/csv/?folder_id=${folderIds}`;
    } else if (exportFormat === "markdown") {
      if (isSelectAll) {
        url = `${apiUrl}/export/questions/markdown/`;
      } else {
        const folderIds = selectedFoldersForExport.map((folder) => folder.id).join(",");
        url = `${apiUrl}/export/questions/markdown/?folder_id=${folderIds}`;
      }
    }

    const response = await axios.get(url, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    });

    const filename = generateExportFilename();
    const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = urlBlob;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(urlBlob);

    setSnackbar({
      open: true,
      message: `✅ Exported as: ${filename}`,
      severity: "success",
    });

    setSelectedFoldersForExport([]);
    setCustomExportFilename("");
  } catch (error) {
    console.error("Export Error:", error);
    setSnackbar({
      open: true,
      message: `❌ Export failed: ${error.response?.data?.message || "Check token or server."}`,
      severity: "error",
    });
  } finally {
    setOpenDialog(false);
  }
};


// Generate filename based on custom input or auto-generate
const generateExportFilename = () => {
  const timestamp = new Date().toISOString().slice(0, 10);
  const formatExt = exportFormat === "csv" ? ".csv" : ".md";

  if (customExportFilename.trim()) {
    let filename = customExportFilename.trim().replace(/\.[^/.]+$/, "");
    filename = filename.replace(/[^a-z0-9_\-\.]/gi, "_");
    return `${filename}_${timestamp}${formatExt}`;
  }

  if (selectedFoldersForExport.length === 1) {
    return `${selectedFoldersForExport[0].name}_${timestamp}${formatExt}`;
  } else if (selectedFoldersForExport.length <= 3) {
    const folderNames = selectedFoldersForExport.map((f) => f.name).join("_");
    return `${folderNames}_${timestamp}${formatExt}`.substring(0, 100);
  } else {
    return `${selectedFoldersForExport.length}_Folders_Export_${timestamp}${formatExt}`;
  }
}; 

// Generate preview filename (for display in dialog)
const generatePreviewFilename = () => {
  const timestamp = new Date().toISOString().slice(0, 10);
  const formatExt = exportFormat === "csv" ? ".csv" : ".md";

  if (customExportFilename.trim()) {
    let filename = customExportFilename.trim().replace(/\.[^/.]+$/, ""); // Remove existing extension
    filename = filename.replace(/[^a-z0-9_\-\.]/gi, "_");
    return `${filename}_${timestamp}${formatExt}`;
  }

  if (selectedFoldersForExport.length === 1) {
    return `${selectedFoldersForExport[0].name}_${timestamp}${formatExt}`;
  } else if (selectedFoldersForExport.length <= 3) {
    const folderNames = selectedFoldersForExport.map((f) => f.name).join("_");
    return `${folderNames}_${timestamp}${formatExt}`.substring(0, 100);
  } else {
    return `${selectedFoldersForExport.length}_Folders_Export_${timestamp}${formatExt}`;
  }
};

// const handleExportJSON = async () => {
//   const token = sessionStorage.getItem('authToken');
//   try {
//     const folderIds = selectedFolders.join(',');
//     const response = await axios.get(`${apiUrl}/export/questions/json/?folder_id=${folderIds}`, {
//       responseType: 'blob',
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });
//     const url = window.URL.createObjectURL(new Blob([response.data]));
//     const link = document.createElement('a');
//     link.href = url;
//     link.setAttribute('download', 'questions.json');
//     document.body.appendChild(link);
//     link.click();
//     link.remove();
//     setSnackbar({
//       open: true,
//       message: '✅ JSON export successful.',
//       severity: 'success',
//     });
//     // Clear selection after successful export
//     setSelectedFolders([]);
//   } catch (error) {
//     console.error('Export JSON Error:', error);
//     setSnackbar({
//       open: true,
//       message: '❌ JSON export failed: ' + (error.response?.data?.message || 'Check token or server.'),
//       severity: 'error',
//     });
//   } finally {
//     setOpenDialog(false);
//   }
// };


  //  const handleSnackbarClose = () => {
  //   setSnackbar({ ...snackbar, open: false });
  // };



  const moveItem = (dragIndex, hoverIndex) => {
    const updatedItems = [...items];
    const draggedItem = updatedItems.splice(dragIndex, 1)[0];
    updatedItems.splice(hoverIndex, 0, draggedItem);
    setItems(updatedItems);
  };

const drawerItems = [
  {
    text: <TreeView 
      setActivePage={setActivePage} 
      selectedFolderId={selectedFolderId}
      setSelectedFolderId={setSelectedFolderId}
      expandedFolders={expandedFolders}
      setExpandedFolders={setExpandedFolders}
    />,
    pageName: null
  },
];

  const createMenuItems = ["Folder", "Subfolder"];

  const menuItems = [
    {
      text: "Import Flashcard",
      icon: <ImportExportIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} />,
      onClick: () => {
        if (userRole === "admin" || permissions.can_import_folder) {
          setImportDialog(true);
        } else {
          setSnackbar({
            open: true,
            message: "You do not have permission for import.",
            severity: "error",
          });
        }
      },
    },
    {
      text: "Export Flashcard",
      icon: <FlashOnOutlinedIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} />,
      onClick: () => {
        if (userRole === "admin" || permissions.can_export_folder) {
          setOpenDialog(true);
        } else {
          setSnackbar({
            open: true,
            message: "You do not have permission for export.",
            severity: "error",
          });
        }
      },
    },
   {
  text: "Export Template",
  icon: <BackupIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} />,
  onClick: () => {
    if (userRole === "admin" || permissions.can_import_folder) {
      setOpenTemplateDialog(true); // Open dialog instead of directly exporting
    } else {
      setSnackbar({
        open: true,
        message: "You do not have permission to export the template.",
        severity: "error",
      });
    }
  },
},
    { text: "Upgrade to Premium", icon: <StarIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} /> },
    // { text: "Settings", icon: <SettingsIcon />, route: "/profilesetting" },
    // { text: "Theme Settings", icon: <ColorLensIcon />, route: "/theme-settings" },
    // { text: "Backup", icon: <BackupIcon />, onClick: () => navigate("/backup") },


    // { text: "Trash", icon: <DeleteIcon /> },
    {
      text: "Custom Theme",
      icon: (
        <ColorLensIcon
          sx={{
            color:
              mode === "dark"
                ? "#44a6e9"
                : mode === "custom"
                  ? customColors.secondary
                  : "#44a6e9",
          }}
        />
      ),
      onClick: () => setOpenThemeModal(true), // 👈 open modal
    },
    { text: "Backup", icon: <BackupIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} />, onClick: () => navigate("/backup") },
  ];




  const fullName = sessionStorage.getItem("fullName") || "User";

  const handleLogoutModalOpen = () => {
    setLogoutModalOpen(true);
  };

  const handleLogoutModalClose = () => {
    setLogoutModalOpen(false);
  };

  const formatDuration = (durationString) => {
    try {
      const [hours, minutes, seconds] = durationString.split(':');
      const h = parseInt(hours);
      const m = parseInt(minutes);
      const s = parseInt(seconds.split('.')[0]); // Ignore microseconds

      if (h === 0 && m === 0) {
        return `${s} sec`;
      } else if (h === 0) {
        return `${m}:${s.toString().padStart(2, '0')}`; // MM:SS
      }
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`; // H:MM:SS
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString;
    }
  };

  // Updated function to format ISO time to MM/DD/YYYY HH:MM AM/PM
  const formatTime = (isoTimeString) => {
    try {
      console.log("Raw input to formatTime:", isoTimeString);
      const date = new Date(isoTimeString);
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date");
      }
      const datePart = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }); // e.g., "03/20/2025"
      const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(' ', ''); // e.g., "06:00AM"
      const formatted = `${timePart}  ${datePart}`; // Time first, then space, then date
      console.log("Formatted time output:", formatted); // Debug to confirm
      return formatted;
    } catch (error) {
      console.error('Error formatting time:', error);
      return isoTimeString;
    }
  };

  useEffect(() => {
    const initializeSessionActivity = () => {
      const token = sessionStorage.getItem("authToken");
      if (token && !sessionStorage.getItem("sessionActivity")) {
        // Initialize session activity with login time
        const sessionActivity = {
          sessionDuration: "N/A",
          loginTime: formatTime(new Date().toISOString()),
          logoutTime: "N/A",
        };
        sessionStorage.setItem("sessionActivity", JSON.stringify(sessionActivity));
      }
    };

    initializeSessionActivity();
  }, []);

  //  logout handler with extra logging
  const handleLogout = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const sessionId = sessionStorage.getItem("sessionId");

      if (!token) {
        handleErrorModalOpen("No authentication token found. Please log in again.");
        return;
      }

      const response = await fetch(`${apiUrl}/logout/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        handleErrorModalOpen(`Logout failed with status: ${response.status}`);
        return;
      }

      const data = await response.json();

      // Retrieve existing session activity and update it
      const existingSessionActivity = sessionStorage.getItem("sessionActivity");
      const sessionActivity = existingSessionActivity
        ? {
            ...JSON.parse(existingSessionActivity),
            sessionDuration: formatDuration(data.session_duration || "N/A"),
            logoutTime: formatTime(data.logout_time || new Date().toISOString()),
          }
        : {
            sessionDuration: formatDuration(data.session_duration || "N/A"),
            loginTime: formatTime(data.login_time || new Date().toISOString()),
            logoutTime: formatTime(data.logout_time || new Date().toISOString()),
          };

      sessionStorage.setItem("sessionActivity", JSON.stringify(sessionActivity));

      localStorage.removeItem("userProfile");
      localStorage.removeItem("profileId");

      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("fullName");
      sessionStorage.removeItem("sessionId");

      navigate("/", { replace: true });
      window.history.pushState(null, null, window.location.href);
      window.onpopstate = () => window.history.go(1);
    } catch (error) {
      console.error("Error during logout:", error.message);
      handleErrorModalOpen("Error during logout: Network error or server unavailable.");
    }
  };

  // Add new state variables at the top of the component's state declarations
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Add useEffect for inactivity tracking
  useEffect(() => {
    const timeoutDuration = 20 * 60 * 1000; // 20 minutes in milliseconds

    const resetTimeout = () => {
      setLastActivity(Date.now());
    };

    const checkInactivity = () => {
      if (Date.now() - lastActivity >= timeoutDuration && !isLoggedOut) {
        setIsLoggedOut(true);
        // Clear session data
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("fullName");
        sessionStorage.removeItem("sessionId");
      }
    };

    // Events to reset activity timer
    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetTimeout));

    // Set up interval to check inactivity
    const interval = setInterval(checkInactivity, 1000);

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimeout));
      clearInterval(interval);
    };
  }, [lastActivity, isLoggedOut]);

  // Add new state variable at the top of the component's state declarations, below existing states
  const [isFlashcardLoggedOut, setIsFlashcardLoggedOut] = useState(false);

  // Add useEffect for flashcard session expiration, below the existing useEffect for 20-minute inactivity
  useEffect(() => {
    const sessionStartTime = Date.now();
    const flashcardSessionDuration = 300 * 60 * 1000; // 300 minutes in milliseconds

    const checkSessionExpiration = () => {
      if (Date.now() - sessionStartTime >= flashcardSessionDuration && !isFlashcardLoggedOut) {
        setIsFlashcardLoggedOut(true);
        // Clear session data
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("fullName");
        sessionStorage.removeItem("sessionId");
      }
    };

    // Set up interval to check session expiration
    const sessionInterval = setInterval(checkSessionExpiration, 1000);

    return () => {
      clearInterval(sessionInterval);
    };
  }, [isFlashcardLoggedOut]);

  return (
    // <DndProvider backend={HTML5Backend}>
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        open={open}
        drawerWidth={drawerWidth}
        sx={{
          backgroundColor:
            mode === "dark"
              ? "#000000ff"
              : mode === "custom"
                ? (customColors.bgNav || "#f5f5f5")
                : "#F5F5F5", // light mode
          color:
            mode === "dark"
              ? "#f6f1f1ff"
              : mode === "custom"
                ? (customColors.text || "#111111")
                : "#302c2cff", // light mode
                 boxShadow: "none", 
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              color: "white",
            }}
          >
            <MenuIcon />
          </IconButton>
<Typography
  variant="h6"
  noWrap
  component="div"
  className="custom-typography"
  sx={{
    color: mode === 'dark' ? '#44a6e9' : mode === 'custom' ? customColors.text : '#142be1',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  }}
>
  {parentFolderName && subfolder_id ? (
    <>
      <Box
        component="span"
        sx={{
          cursor: 'pointer',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.3px',
          fontSize: '1.5rem',
          transition: 'all 0.2s ease',
          background: mode === "dark"
            ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
            : mode === "custom"
              ? customColors.text || "#f5f5f5"
              : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: 'text',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => navigate(`/cards/${parentFolderId}`)}
      >
        {parentFolderName}
      </Box>
      <Box component="span" sx={{ mx: 1, color: 'text.disabled', fontWeight: 300, fontSize: '1.5rem' }}>
        <NavigateNextIcon />
      </Box>
      <Box
        component="span"
        sx={{
          cursor: 'pointer',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.3px',
          fontSize: '1.5rem',
          transition: 'all 0.2s ease',
          background: mode === "dark"
            ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
            : mode === "custom"
              ? customColors.text || "#f5f5f5"
              : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: 'text',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => navigate(`/cards/${subfolder_id}`)}
      >
        {subfolderName}
      </Box>
    </>
  ) : (
    <Box
      component="span"
      variant="h4"
      sx={{
        textTransform: 'uppercase',
        fontWeight: 600,
        letterSpacing: '0.3px',
        fontSize: '1.5rem',
        transition: 'all 0.2s ease',
        fontWeight: "bolder",
        background: mode === "dark"
          ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)"
          : mode === "custom"
            ? customColors.text || "#f5f5f5"
            : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: 'text',
      }}
    >
      {currentPage}
    </Box>
  )}
</Typography>

         
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="See KaTeX reference to add mathematical expression" arrow>
            <IconButton
              sx={{color:"primary.dark", '&:focus': { outline: 'none' }}}
              component="a"
              href="https://katex.org/docs/supported.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DescriptionIcon />
            </IconButton>
          </Tooltip>
          <ThemeToggle />

          <IconButton  sx={{color:"primary.dark",'&:focus': { outline: 'none' }}} onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                background: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#ffffff",
                color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text : "#000000",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => {
                  handleMenuClose();
                  if (!item.route && item.onClick) {
                    item.onClick();
                  }
                }}
                component={item.route ? Link : "div"}
                to={item.route || undefined}
                sx={{
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f0f0f0",
                  },
                  display: item.text === "Backup" && userRole !== "admin" ? "none" : "flex",
                }}
              >
                <ListItemIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleActivityModalOpen();
              }}
              sx={{
                "&:hover": {
                  backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f0f0f0",
                },
              }}
            >
              <ListItemIcon>
                <AccessTimeIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }} />
              </ListItemIcon>
              <ListItemText primary="Activity Status" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogoutModalOpen();
              }}
              sx={{
                "&:hover": {
                  backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f0f0f0",
                },
              }}
            >
              <ListItemIcon sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    

<Drawer 
  variant="permanent" 
  open={open} 
  drawerWidth={drawerWidth}
  PaperProps={{
    sx: {
      backgroundColor:
        mode === "dark"
          ? "#121212"
          : mode === "custom"
            ? customColors.bgNav || "#fff"
            : "#F5F5F5",
      color: mode === "dark" ? "#fff" : "#000"
    }
  }}
>
  <DrawerHeader
    sx={{
      background: mode === "dark" ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" : mode === "custom" ? customColors.bgProfile : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
        backgroundSize: "200% 200%",
        animation: "shimmer 3s infinite",
      },
      "@keyframes shimmer": {
        "0%": { backgroundPosition: "-200% 0" },
        "100%": { backgroundPosition: "200% 0" },
      },
    }}
  >
    {/* Profile Section */}
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <IconButton
        onClick={handleProfileModalOpen}
        sx={{
          padding: 0,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <Tooltip title="View Profile">
          <div
            style={{
              background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
              width: "52px",
              height: "52px",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "26px",
              color: "white",
              border: "3px solid white",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.4)",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(37, 99, 235, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.4)";
            }}
          >
            🎓
          </div>
        </Tooltip>
      </IconButton>
      {open && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1.2rem",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <span>Welcome, {fullName.split(" ")[0]}!</span>
          <span style={{ fontWeight: 500, fontSize: "1rem", color: "#e0e0e0" }}>
            {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : "N/A"}
          </span>
        </Typography>
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <IconButton
        onClick={handleDrawerClose}
        sx={{
          color: "white",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        {theme.direction === "rtl" ? (
          <ChevronRightIcon />
        ) : (
          <ChevronLeftIcon />
        )}
      </IconButton>
    </Box>
  </DrawerHeader>

  {/* Navigation Links - Fixed */}
  {/* Student Profile Modal */}
        <Dialog
          open={profileModalOpen}
          onClose={handleProfileModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(to right, #142be1, #44a6e9)",
              color: "white",
              textAlign: "center",
              py: 2,
              fontWeight: "bold",
            }}
          >
            Your Profile
          </DialogTitle>
          <DialogContent sx={{ py: 3, background: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bgNav : "#ffffff" }}>
            <Box sx={{ mt: 2, }}>
              {/* Personal Information */}
              <Typography variant="h6" sx={{ color: "#142be1", mb: 1, fontWeight: "500" }}>
                Personal Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <TextField
                  label="Admission Number"
                  value={profileData.admission_number || ""}
                  onChange={(e) => handleProfileChange("admission_number", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
                <TextField
                  label="Name"
                  value={fullName
                    .split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ")}
                  onChange={(e) => handleProfileChange("name", e.target.value)}
                  disabled={isProfileEditing}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#ffffff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />


                <TextField
                  label="Date of Joining"
                  type={isProfileEditing ? "date" : "text"}
                  value={profileData.date_of_joining || ""}
                  onChange={(e) => handleProfileChange("date_of_joining", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                    "& input[type='date']::-webkit-calendar-picker-indicator": {
                      filter: mode === "dark" ? "invert(1)" : "invert(0)", // white in darkMode, default in light
                      cursor: "pointer",
                    },
                  }}
                />
                <TextField
                  label="Date of Birth"
                  type={isProfileEditing ? "date" : "text"}
                  value={profileData.dob || ""} // Fixed: Use dob instead of date_of_joining
                  onChange={(e) => handleProfileChange("dob", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff",// darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                    "& input[type='date']::-webkit-calendar-picker-indicator": {
                      filter: mode === "dark" ? "invert(1)" : "invert(0)", // white in darkMode, default in light
                      cursor: "pointer",
                    },
                  }}
                />



                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="gender-label" sx={{ color: "#142be1", fontWeight: "500" }}>
                    Gender
                  </InputLabel>
                  <Select
                    labelId="gender-label"
                    label="Gender"
                    value={profileData.gender || ""}
                    onChange={(e) => {
                      if (isProfileEditing) {
                        handleProfileChange("gender", e.target.value);

                      }
                    }}
                    onClick={(e) => {
                      if (!isProfileEditing) {
                        e.preventDefault(); // prevent dropdown from opening
                      }
                    }}
                    sx={{
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff",
                      borderRadius: "8px",
                      pointerEvents: isProfileEditing ? "auto" : "none",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.primary : "#142be1" }}>
                      <em>Select Gender</em>
                    </MenuItem>
                    {genderOptions.map((option) => (
                      <MenuItem key={option} value={option} sx={{ color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.primary : "#142be1" }}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>

                </FormControl>
                {/* Photo Upload */}
                {isProfileEditing && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="body2" sx={{ color: "#142be1", fontWeight: "500" }}>
                      Upload Photo
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      disabled={!isProfileEditing}
                    />
                  </Box>
                )}
                {!isProfileEditing && profileData.photo && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="body2" sx={{ color: "#142be1", fontWeight: "500" }}>
                      Profile Photo
                    </Typography>
                    <img
                      src={profileData.photo}
                      alt="Profile"
                      style={{ width: "100px", height: "100px", borderRadius: "8px" }}
                    />
                  </Box>
                )}
              </Box>

              {/* Family Information */}
              <Typography variant="h6" sx={{ color: "#142be1", mb: 1, fontWeight: "500" }}>
                Family Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <TextField
                  label="Father's Name"
                  value={profileData.fathers_name || ""}
                  onChange={(e) => handleProfileChange("fathers_name", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
                <TextField
                  label="Mother's Name"
                  value={profileData.mothers_name || ""}
                  onChange={(e) => handleProfileChange("mothers_name", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>
              {/* Academic Information */}
              <Typography variant="h6" sx={{ color: "#142be1", mb: 1, fontWeight: "500" }}>
                Academic Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <TextField
                  label="Student Class"
                  value={profileData.student_class || ""}
                  onChange={(e) => handleProfileChange("student_class", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
                <TextField
                  label="School"
                  value={profileData.school || ""}
                  onChange={(e) => handleProfileChange("school", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>

              {/* Contact Information */}
              <Typography variant="h6" sx={{ color: "#142be1", mb: 1, fontWeight: "500" }}>
                Contact Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={profileData.email || ""}
                  onChange={(e) => handleProfileChange("email", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
                <TextField
                  label="Alternate Email"
                  type="email"
                  value={profileData.alternate_email || ""}
                  onChange={(e) => handleProfileChange("alternate_email", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>
              {/* Address Information */}
              <Typography variant="h6" sx={{ color: "#142be1", mb: 1, fontWeight: "500" }}>
                Address Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <TextField
                  label="Permanent Address"
                  value={profileData.permanent_address || ""}
                  onChange={(e) => handleProfileChange("permanent_address", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
                <TextField
                  label="Current Address"
                  value={profileData.current_address || ""}
                  onChange={(e) => handleProfileChange("current_address", e.target.value)}
                  InputProps={{ readOnly: !isProfileEditing }}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bgNav : "#f0f7ff", // darker bg for dark mode
                      borderRadius: "8px",
                    },
                    "& .MuiInputLabel-root": {
                      color: mode === "dark" ? "#4DB6FF" : mode === "custom" ? customColors.text : "#142be1", // soft blue in dark mode
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 2, backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f0f7ff" }}>
            {!profileCreated && !isProfileEditing && (
              <Button
                onClick={() => {
                  if (!permissions.can_create_profile) {
                    setSnackbar({
                      open: true,
                      message: "You do not have permission to create a profile.",
                      severity: "error",
                    });
                    return;
                  }
                  handleProfileEditToggle();
                }}
                variant="contained"
                disabled={!permissions.can_create_profile}
                sx={{
                  backgroundColor: permissions.can_create_profile ? "#44a6e9" : "#c5e1a5",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    backgroundColor: permissions.can_create_profile ? "#2a8ed4" : "#c5e1a5",
                    boxShadow: permissions.can_create_profile ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#c5e1a5",
                    color: "#ffffff",
                    // Ensure the button remains clickable even when disabled
                    pointerEvents: "auto",
                    cursor: permissions.can_create_profile ? "pointer" : "not-allowed",
                  },
                }}
              >
                Create Profile
              </Button>
            )}
            {profileCreated && !isProfileEditing && (
              <Button
                onClick={handleProfileEditToggle}
                variant="contained"
                sx={{
                  backgroundColor: "#44a6e9",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#2a8ed4",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                Edit
              </Button>
            )}
            {isProfileEditing && (
              <Button
                onClick={handleProfileEditToggle}
                variant="contained"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f57c00",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                Cancel
              </Button>
            )}
            {isProfileEditing && (
              <Button
                onClick={handleProfileSave}
                variant="contained"
                disabled={
                  (!hasUnsavedChanges || Object.values(validationErrors).some((error) => error))
                }
                sx={{
                  backgroundColor: (permissions.can_update_profile || !profileCreated) ? "#4caf50" : "#c5e1a5",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    backgroundColor: (permissions.can_update_profile || !profileCreated) ? "#388e3c" : "#c5e1a5",
                    boxShadow: (permissions.can_update_profile || !profileCreated) ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#c5e1a5",
                    color: "#ffffff",
                    // Ensure the button remains clickable even when disabled
                    pointerEvents: "auto",
                    cursor: (permissions.can_update_profile || !profileCreated) ? "pointer" : "not-allowed",
                  },
                }}
              >
                {profileCreated ? "Update" : "Save"}
              </Button>
            )}
            <Button
              onClick={handleProfileModalClose}
              variant="contained"
              sx={{
                backgroundColor: "#e91e63",
                color: "white",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                px: 4,
                py: 1,
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  backgroundColor: "#c2185b",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>

          {/* Cancel Confirmation Dialog */}
          <Dialog
            open={cancelConfirmationOpen}
            onClose={handleDenyCancel}
            aria-labelledby="cancel-confirmation-dialog-title"
          >
            <DialogTitle
              id="cancel-confirmation-dialog-title"
              sx={{
                background: mode === "dark" ? "linear-gradient(to right, #3d6580, #435864)" : mode === "custom" ? customColors.background : "linear-gradient(to right, #142be1, #44a6e9)",
                color: "white",
                textAlign: "center",
                py: 2,
                fontWeight: "bold",
              }}
            >
              Unsaved Changes
            </DialogTitle>
            <DialogContent sx={{ py: 3, background: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bgNav : "#ffffff" }}>
              <DialogContentText sx={{ textAlign: "center", color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#666" }}>
                You have unsaved changes. Are you sure you want to cancel and discard them?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 2, background: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bgNav : "#ffffff" }}>
              <Button
                onClick={handleDenyCancel}
                variant="contained"
                sx={{
                  backgroundColor: "#44a6e9",
                  color: "white",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#2a8ed4" },
                }}
              >
                No, Keep Editing
              </Button>
              <Button
                onClick={handleConfirmCancel}
                variant="contained"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f57c00" },
                }}
              >
                Yes, Discard Changes
              </Button>
            </DialogActions>
          </Dialog>
        </Dialog>
  <List>
    <ListItem disablePadding>
      <ListItemButton 
        onClick={() => {
          navigate("/home");
          setActivePage("Home Page");
          setCurrentPage("Home Page");
          setSelectedFolderId(null);
        }}
        selected={activePage === "Home Page"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.2)" : mode === "custom" ? `${customColors.secondary}20` : "rgba(20, 43, 225, 0.1)",
            borderLeft: `4px solid ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`,
            "& .MuiListItemIcon-root": {
              color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1",
            },
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.3)" : mode === "custom" ? `${customColors.secondary}30` : "rgba(20, 43, 225, 0.2)",
            },
          },
        }}
      >
        <ListItemIcon>
          <HomeIcon sx={{color:"primary.dark", '&:focus': { outline: 'none' }}}/>
        </ListItemIcon>
        <ListItemText primary={<strong>Home Page</strong>} />
      </ListItemButton>
    </ListItem>

    <ListItem disablePadding>
      <ListItemButton 
        onClick={() => {
          navigate("/flashpractice");
          setActivePage("FlashCard Practice");
          setSelectedFolderId(null);
          setCurrentPage("FlashCard Practice");
        }}
        selected={activePage === "FlashCard Practice"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.2)" : mode === "custom" ? `${customColors.secondary}20` : "rgba(20, 43, 225, 0.1)",
            borderLeft: `4px solid ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`,
            "& .MuiListItemIcon-root": {
              color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1",
            },
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.3)" : mode === "custom" ? `${customColors.secondary}30` : "rgba(20, 43, 225, 0.2)",
            },
          },
        }}
      >
        <ListItemIcon>
          <BoltIcon sx={{color:"primary.dark", '&:focus': { outline: 'none' }}} />
        </ListItemIcon>
        <ListItemText primary={<strong>FlashCard Practice</strong>} />
      </ListItemButton>
    </ListItem>

    <ListItem disablePadding>
      <ListItemButton 
        onClick={() => {
          navigate("/allquiz");
          setCurrentPage("Quizzes Practice");
          setActivePage("Quizzes Practice");
          setSelectedFolderId(null);
        }}
        selected={activePage === "Quizzes Practice"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.2)" : mode === "custom" ? `${customColors.secondary}20` : "rgba(20, 43, 225, 0.1)",
            borderLeft: `4px solid ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`,
            "& .MuiListItemIcon-root": {
              color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1",
            },
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.3)" : mode === "custom" ? `${customColors.secondary}30` : "rgba(20, 43, 225, 0.2)",
            },
          },
        }}
      >
        <ListItemIcon>
          <QuizIcon sx={{color:"primary.dark", '&:focus': { outline: 'none' }}} />
        </ListItemIcon>
        <ListItemText primary={<strong>Quiz Practice</strong>} />
      </ListItemButton>
    </ListItem>
  </List>

  <Divider />

  {/* Scrollable TreeView Section - THIS IS THE KEY PART */}
  <Box sx={{ 
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: 0,        // CRITICAL!
    minHeight: 0,     // CRITICAL!
  }}>
    <List sx={{ 
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      padding: 0
    }}>
      {drawerItems.map((item) => (
        <ListItem key={item.text} disablePadding sx={{ display: "block", height: '100%', overflow: 'hidden' }}>
        {/* // <ListItem key={item.text} disablePadding sx={{ display: "block" }}> */}

          <ListItemButton
            onClick={item.onClick}
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              "&:hover": {
                backgroundColor: "transparent !important",
              },
              "&.Mui-focusVisible": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
                color: "inherit",
                display: 'none', // Hide icon since TreeView has its own
              }}
            >
              {item.icon}
            </ListItemIcon>
            <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
              {item.text}
            </Box>
          </ListItemButton>
        </ListItem>
      ))}
      {newItemType && (
        <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
          <TextField
            label={`Enter ${newItemType} Name`}
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyDown={handleNewItemSubmit}
            fullWidth
            size="small"
          />
        </Box>
      )}
      {items.map((item, index) => (
        <DraggableListItem
          key={index}
          item={item}
          index={index}
          moveItem={moveItem}
          setEditingIndex={setEditingIndex}
        />
      ))}
    </List>
  </Box>

  {/* Control Panel Section - Fixed at Bottom */}
  <List style={{ marginTop: 2, borderTop: "1px solid #e0e0e0" }}>
    <ListItem disablePadding>
      <ListItemButton 
        onClick={() => {
          navigate("/control-panel");
          setCurrentPage("Control Panel");
          setActivePage("Control Panel");
          setSelectedFolderId(null);
        }}
        selected={activePage === "Control Panel"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.2)" : mode === "custom" ? `${customColors.secondary}20` : "rgba(20, 43, 225, 0.1)",
            borderLeft: `4px solid ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`,
            "& .MuiListItemIcon-root": {
              color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1",
            },
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.3)" : mode === "custom" ? `${customColors.secondary}30` : "rgba(20, 43, 225, 0.2)",
            },
          },
        }}
      >
        <ListItemIcon sx={{color:"primary.dark", '&:focus': { outline: 'none' }}}>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary={<strong>Control Panel</strong>} />
      </ListItemButton>
    </ListItem>
  </List>

  {/* Resize Handle */}
  <Box
    sx={{
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      width: "5px",
      cursor: "col-resize",
      zIndex: theme.zIndex.drawer + 1,
    }}
    onMouseDown={handleMouseDown}
  />
</Drawer>
      <Box component="main" sx={{ flexGrow: 1, paddingRight:2 }}>
        <DrawerHeader />
        <main>
          {/* <Flashcard /> */}
          {content}
        </main>
      </Box>
      <Menu
        anchorEl={createAnchorEl}
        open={Boolean(createAnchorEl)}
        onClose={() => handleCreateMenuClose()}
      >
        {createMenuItems.map((type) => (
          <MenuItem key={type} onClick={() => handleCreateMenuClose(type)}>
            <ListItemIcon>{getIcon(type)}</ListItemIcon>
            <ListItemText primary={type} />
          </MenuItem>
        ))}
      </Menu>
      {/* // Add new modal for auto-logout within the return statement, just before the closing Box */}
      {/* sx={ darkMode ? { background: '#121212', color: '#fff' } : { background: '#fff', color: '#000' }} */}
      <Dialog
  open={isLoggedOut}
  disableEscapeKeyDown
  onClose={() => {}}
  PaperProps={{
    sx: {
      borderRadius: '24px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: mode === "dark"
        ? '0 24px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        : '0 24px 48px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.05)',
      overflow: 'hidden',
      background: mode === "dark"
        ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
        : mode === "custom"
          ? customColors.background
          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      position: 'relative'
    }
  }}
  sx={{
    backdropFilter: 'blur(16px)',
    zIndex: 1400,
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.6)'
    }
  }}
>
  {/* Decorative gradient orb */}
  <Box sx={{
    position: 'absolute',
    top: '-80px',
    right: '-80px',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
    filter: 'blur(40px)'
  }} />

  <Box sx={{
    position: 'absolute',
    bottom: '-60px',
    left: '-60px',
    width: '160px',
    height: '160px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(30, 139, 175, 0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
    filter: 'blur(40px)'
  }} />

  {/* Content Container */}
  <Box sx={{ 
    position: 'relative',
    zIndex: 1,
    p: 4,
    textAlign: 'center'
  }}>
    {/* Icon with animated background */}
    <Box sx={{
      width: '72px',
      height: '72px',
      margin: '0 auto 24px',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: mode === "dark"
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 139, 175, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(30, 139, 175, 0.08) 100%)',
      border: mode === "dark"
        ? '1px solid rgba(59, 130, 246, 0.2)'
        : '1px solid rgba(59, 130, 246, 0.15)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-2px',
        borderRadius: '20px',
        padding: '2px',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(30, 139, 175, 0.3))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.6
      }
    }}>
      <LockClockIcon sx={{
        fontSize: '36px',
        color: mode === "dark" ? '#60a5fa' : '#3b82f6',
        filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))'
      }} />
    </Box>

    {/* Title */}
    <DialogTitle sx={{
      color: mode === "dark"
        ? '#f1f5f9'
        : mode === "custom"
          ? customColors.text
          : '#0f172a',
      fontWeight: 700,
      fontSize: '1.75rem',
      p: 0,
      mb: 1,
      letterSpacing: '-0.02em',
      lineHeight: 1.2
    }}>
      Session Expired
    </DialogTitle>

    {/* Subtitle with icon */}
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      mb: 3,
      px: 2,
      py: 0.75,
      borderRadius: '12px',
      background: mode === "dark"
        ? 'rgba(248, 113, 113, 0.1)'
        : 'rgba(239, 68, 68, 0.06)',
      border: mode === "dark"
        ? '1px solid rgba(248, 113, 113, 0.2)'
        : '1px solid rgba(239, 68, 68, 0.12)'
    }}>
      <Box sx={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: '#ef4444',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 }
        }
      }} />
      <Typography sx={{
        color: mode === "dark" ? '#fca5a5' : '#dc2626',
        fontSize: '0.875rem',
        fontWeight: 600,
        letterSpacing: '0.01em'
      }}>
        Inactive for 20 minutes
      </Typography>
    </Box>

    {/* Description */}
    <Typography sx={{
      color: mode === "dark"
        ? 'rgba(241, 245, 249, 0.7)'
        : mode === "custom"
          ? customColors.textSecondary
          : 'rgba(15, 23, 42, 0.65)',
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      mb: 3,
      px: 1,
      fontWeight: 400
    }}>
      For your security, you've been automatically logged out. Please sign in again to continue where you left off.
    </Typography>

    {/* Action Button */}
    <Button
      onClick={() => {
        setIsLoggedOut(false);
        navigate('/', { replace: true });
      }}
      variant="contained"
      fullWidth
      sx={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e8baf 100%)',
        color: 'white',
        fontWeight: 600,
        py: 1.5,
        borderRadius: '14px',
        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease'
        },
        '&:hover': {
          background: 'linear-gradient(135deg, #2563eb 0%, #1a7a9a 100%)',
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
          transform: 'translateY(-2px)',
          '&::before': {
            opacity: 1
          }
        },
        '&:active': {
          transform: 'translateY(0px)'
        },
        textTransform: 'none',
        fontSize: '1rem',
        letterSpacing: '0.01em'
      }}
    >
      Sign In Again
    </Button>
  </Box>
</Dialog>

      {/* activity status modal */}
      <Dialog
        open={activityModalOpen}
        onClose={handleActivityModalClose}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "#ffffff",
            width: "600px", // Reduced width for a more compact dialog
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: mode === "dark" ? "#304668ff" : mode === "custom" ? customColors.primary : "#0d47a1",// Darker blue for header
            color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#fff",
            textAlign: "center",
            py: 1.5,
            fontWeight: 500,
            fontSize: "1.2rem",
          }}
        >
          Session Activity
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2, backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.background : "#fff", textAlign: "center" }}>
          {sessionDetails ? (
            <TableContainer component={Paper} sx={{ borderRadius: "6px", boxShadow: "none", marginTop: 1 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#e3f2fd" }}>
                    <TableCell sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", fontWeight: 500, fontSize: "0.9rem", borderBottom: "1px solid #e0e0e0", py: 1 }}>
                      Session Duration
                    </TableCell>
                    <TableCell sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", fontWeight: 500, fontSize: "0.9rem", borderBottom: "1px solid #e0e0e0", py: 1 }}>
                      Login Time
                    </TableCell>
                    <TableCell sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", fontWeight: 500, fontSize: "0.9rem", borderBottom: "1px solid #e0e0e0", py: 1 }}>
                      Logout Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ p: 1, border: "none", fontSize: "0.9rem", color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f5faff" }}>
                      {sessionDetails.sessionDuration}
                    </TableCell>
                    <TableCell sx={{ p: 1, border: "none", fontSize: "0.9rem", color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f5faff" }}>
                      {sessionDetails.loginTime}
                    </TableCell>
                    <TableCell sx={{ p: 1, border: "none", fontSize: "0.9rem", color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f", backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.background : "#f5faff" }}>
                      {sessionDetails.logoutTime}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#37474f",
                fontSize: "0.9rem",
                fontWeight: 400,
                py: 2,
              }}
            >
              No session activity data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, px: 2, bgcolor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.background : "#fff" }}>
          <Button
            onClick={handleActivityModalClose}
            variant="contained"
            sx={{
              backgroundColor: mode === "dark" ? "#1565c0" : mode === "custom" ? customColors.primary : "#1565c0",
              color: "#fff",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "0.9rem",
              px: 3,
              py: 0.5,
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: mode === "dark" ? '#142be1' : mode === "custom" ? customColors.background : "#0288d1",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* // Add modal for flashcard session expiration within the return statement,  */}
      <Dialog
        open={isFlashcardLoggedOut}
        disableEscapeKeyDown
        onClose={() => { }}
        sx={{ backdropFilter: 'blur(4px)' }}
      >
        <DialogTitle
          sx={{
            background: mode === "dark" ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === "custom" ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)',
            color: 'white',
            textAlign: 'center',
            py: 2,
            fontWeight: 'bold',
          }}
        >
          Your Session has expired
        </DialogTitle>
        <DialogContent sx={{ py: 3, backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff" }}>
          <Typography sx={{ textAlign: 'center', color: mode === 'dark' ? '#fff' : mode === "custom" ? customColors.text : '#666' }}>
            Your session has expired after 300 minutes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bg : "#f0f7ff" }}>
          <Button
            onClick={() => {
              setIsFlashcardLoggedOut(false);
              navigate('/', { replace: true });
            }}
            variant="contained"
            sx={{
              backgroundColor: mode === 'dark' ? "#1565c0" : mode === "custom" ? customColors.primary : '#44a6e9',
              '&:hover': { backgroundColor: mode === 'dark' ? '#142be1' : mode === "custom" ? customColors.background : '#142be1' },
              px: 4,
              py: 1,
              borderRadius: 1,
            }}
          >
            Login Again
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>



      {/* Add error modal to the return statement */}
      <Dialog open={errorModal.open} onClose={handleErrorModalClose}>
        <DialogTitle sx={{
          background: mode === 'dark' ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === 'custom' ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)',
          color: 'white',
          textAlign: 'center',
          py: 2,
          fontWeight: 'bold'
        }}>
          Error
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 2, backgroundColor: mode === 'dark' ? '#121212' : mode === 'custom' ? customColors.bg : '#fff' }}>
          <Typography sx={{ textAlign: 'center', color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#666' }}>
            {errorModal.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={handleErrorModalClose}
            variant="contained"
            sx={{
              backgroundColor: mode === 'dark' ? "#1565c0" : mode === 'custom' ? customColors.primary : '#44a6e9',
              '&:hover': { backgroundColor: mode === 'dark' ? '#142be1' : mode === 'custom' ? customColors.background : '#142be1' },
              px: 4,
              py: 1,
              borderRadius: 1
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* logout dialog */}
     <Dialog
  open={logoutModalOpen}
  onClose={handleLogoutModalClose}
  maxWidth="xs"
  fullWidth
  sx={{
    "& .MuiDialog-paper": {
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff",
    },
  }}
>
  <DialogTitle
    sx={{
      fontSize: "1.8rem",
      fontWeight: "800",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      paddingBottom: "10px",
      borderBottom: "2px solid #e5e7eb",
      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
      textAlign: "center",
    }}
  >
    <LogoutIcon sx={{ marginRight: "10px", verticalAlign: "middle" }} />
    Confirm Logout
  </DialogTitle>
  <DialogContent
    sx={{
      py: 3,
      backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff",
    }}
  >
    <Typography
      sx={{
        textAlign: "center",
        color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
        fontSize: "1.05rem",
        lineHeight: 1.6,
      }}
    >
      Are you sure you want to log out?
    </Typography>
  </DialogContent>
  <DialogActions
    sx={{
      justifyContent: "center",
      pb: 2,
      gap: 2,
      backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bg : "#fff",
    }}
  >
    <Button
      onClick={handleLogoutModalClose}
      sx={{
        color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
        padding: "10px 24px",
        fontSize: "1rem",
        fontWeight: "600",
        borderRadius: "10px",
        textTransform: "none",
        "&:hover": {
          backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.background : "rgba(0,0,0,0.04)",
        },
      }}
    >
      Cancel
    </Button>
    <Button
  onClick={() => {
    handleLogoutModalClose();
    handleLogout();
  }}
  variant="contained"
  sx={{
    background: "linear-gradient(135deg, #8B0000 0%, #DC143C 100%)", // Dark red gradient
    color: "white",
    padding: "10px 28px",
    fontSize: "1rem",
    fontWeight: "700",
    borderRadius: "10px",
    textTransform: "none",
    boxShadow: "0 4px 15px rgba(139, 0, 0, 0.4)", // Dark red shadow
    "&:hover": {
      background: "linear-gradient(135deg, #DC143C 0%, #8B0000 100%)", // Reverse gradient on hover
      boxShadow: "0 12px 35px rgba(139, 0, 0, 0.5)", // Darker shadow on hover
    },
  }}
>
  <LogoutIcon sx={{ marginRight: "8px", fontSize: "1.2rem" }} />
  Logout
</Button>

  </DialogActions>
</Dialog>


      {/* import dialog */}
    <Dialog open={importDialog} onClose={() => setImportDialog(false)}>
  <DialogTitle
    sx={{
      fontSize: "1.8rem",
      fontWeight: "800",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      paddingBottom: "10px",
      borderBottom: "2px solid #e5e7eb",
      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
    }}
  >
    <ImportExportIcon sx={{ marginRight: "10px", verticalAlign: "middle" }} />
    Import Flashcards
  </DialogTitle>
  <DialogContent sx={{ py: 3, backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff" }}>
    <Typography
      sx={{
        textAlign: "center",
        color: mode === "dark" ? "#ccc" : mode === "custom" ? customColors.textPrimary : "#555",
        mb: 1.5,
        fontSize: "1.05rem",
        fontWeight: 500,
      }}
    >
      Select a file and choose the format to import your flashcards.
    </Typography>
    <Typography
      sx={{
        textAlign: "center",
        color: mode === "dark" ? "#777" : mode === "custom" ? customColors.textSecondary : "#888",
        fontSize: "0.85rem",
        fontWeight: 400,
      }}
    >
      [Note: The file must match the selected format.]
    </Typography>

    {/* Import Format Selection */}
    <Box sx={{ mb: 2, mt: 2 }}>
      <Typography sx={{ mb: 1, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000", fontWeight: "600" }}>
        Import Format
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormControlLabel
          control={
            <Radio
              checked={importFormat === "csv"}
              onChange={() => setImportFormat("csv")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="CSV"
        />
        <FormControlLabel
          control={
            <Radio
              checked={importFormat === "markdown"}
              onChange={() => setImportFormat("markdown")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="Markdown"
        />
      </Box>
    </Box>

    {/* File Input */}
    <input
      type="file"
      accept={importFormat === "csv" ? ".csv" : ".md"}
      onChange={(e) => setSelectedFile(e.target.files[0])}
      style={{
        display: "block",
        margin: "0 auto 20px",
        padding: "10px",
        borderRadius: "8px",
        border: `2px dashed ${mode === "dark" ? "#444" : mode === "custom" ? customColors.border : "#e0e7ff"}`,
        backgroundColor: mode === "dark" ? "#222" : mode === "custom" ? customColors.bg : "#f8f9ff",
        color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#142be1",
        width: "100%",
        textAlign: "center",
      }}
    />
  </DialogContent>
  <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 2, backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bg : "#fff" }}>
    {/* Cancel Button */}
    <Button
      onClick={() => {
        setImportDialog(false);
        setSelectedFile(null);
      }}
      sx={{
        color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
        padding: "10px 24px",
        fontSize: "1rem",
        fontWeight: "600",
        borderRadius: "10px",
        textTransform: "none",
        "&:hover": {
          backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.background : "rgba(0,0,0,0.04)",
        },
      }}
    >
      Cancel
    </Button>

    {/* Import Button */}
    <Button
      onClick={handleImport}
      disabled={!selectedFile || (importFormat === "csv" ? !selectedFile.name.endsWith('.csv') : !selectedFile.name.endsWith('.md'))}
      variant="contained"
      sx={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        color: "white",
        padding: "10px 28px",
        fontSize: "1rem",
        fontWeight: "700",
        borderRadius: "10px",
        textTransform: "none",
        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
        "&:hover": {
          background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
          boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
        },
        "&.Mui-disabled": {
          background: "#d1d5db",
          color: "white",
          opacity: 0.6,
        },
      }}
    >
      <ImportExportIcon sx={{ marginRight: "8px", fontSize: "1.2rem" }} />
      Import {importFormat === "csv" ? "CSV" : "Markdown"}
    </Button>
  </DialogActions>
</Dialog> 

      {/* export dialog */}
<Dialog
  open={openDialog}
  onClose={() => {
    setOpenDialog(false);
    setSelectedFoldersForExport([]);
    setCustomExportFilename("");
  }}
>
  <DialogTitle
    sx={{
      fontSize: "1.8rem",
      fontWeight: "800",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      paddingBottom: "10px",
      borderBottom: "2px solid #e5e7eb",
      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
    }}
  >
    <BackupIcon sx={{ marginRight: "10px", verticalAlign: "middle" }} />
    Export Flashcard
  </DialogTitle>

  <DialogContent sx={{ paddingTop: "24px", backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff" }}>
    {/* Format Selection */}
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ mb: 1, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000", fontWeight: "600" }}>
        Export Format
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormControlLabel
          control={
            <Radio
              checked={exportFormat === "csv"}
              onChange={() => setExportFormat("csv")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="CSV"
        />
        <FormControlLabel
          control={
            <Radio
              checked={exportFormat === "markdown"}
              onChange={() => setExportFormat("markdown")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="Markdown"
        />
      </Box>
    </Box>

    {/* Folder Selection */}
    <FormControl fullWidth sx={{ mt: 1, mb: 3 }}>
      <InputLabel
        id="export-folder-select-label"
        sx={{
          color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
          fontWeight: "700"
        }}
      >
        Select Folders
      </InputLabel>
      <Select
        labelId="export-folder-select-label"
        label="Select Folders"
        multiple
        value={selectedFoldersForExport.map((f) => f.id)}
        onChange={(e) => {
          const selectedIds = e.target.value;
          if (selectedIds.includes("select-all")) {
            if (selectedFoldersForExport.length === folders.length) {
              setSelectedFoldersForExport([]);
            } else {
              setSelectedFoldersForExport([...folders]);
            }
          } else {
            const selectedFolders = folders.filter((f) =>
              selectedIds.includes(f.id)
            );
            setSelectedFoldersForExport(selectedFolders);
          }
        }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((id) => {
              const folder = folders.find((f) => f.id === id);
              return (
                <Chip
                  key={id}
                  label={folder?.name}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setSelectedFoldersForExport((prev) =>
                      prev.filter((f) => f.id !== id)
                    );
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    backgroundColor: mode === "dark" ? "#333" : mode === "custom" ? customColors.primary : "#e0e7ff",
                    color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
                  }}
                />
              );
            })}
          </Box>
        )}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            border: `2px solid ${mode === "dark" ? "#444" : mode === "custom" ? customColors.border : "#e0e7ff"}`,
            backgroundColor: mode === "dark" ? "#222" : mode === "custom" ? customColors.bg : "white",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              "&::-webkit-scrollbar": { width: "10px" },
              "&::-webkit-scrollbar-track": {
                background: mode === "dark" ? "#2d3748" : mode === "custom" ? customColors.bg : "#f1f5f9",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: mode === "dark" ? "#475569" : mode === "custom" ? customColors.secondary : "#94a3b8",
                borderRadius: "6px",
                border: `2px solid ${mode === "dark" ? "#2d3748" : mode === "custom" ? customColors.bg : "#f1f5f9"}`,
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#64748b" },
              scrollbarWidth: "thin",
              scrollbarColor: mode === "dark"
                ? "#475569 #2d3748"
                : mode === "custom"
                  ? `${customColors.secondary} ${customColors.bg}`
                  : "#94a3b8 #f1f5f9",
            },
          },
        }}
      >
        {/* Select All Option */}
        <MenuItem value="select-all">
          <Checkbox
            checked={
              selectedFoldersForExport.length > 0 &&
              selectedFoldersForExport.length === folders.length
            }
            indeterminate={
              selectedFoldersForExport.length > 0 &&
              selectedFoldersForExport.length < folders.length
            }
            sx={{
              color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9",
              "&.Mui-checked": { color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" },
            }}
          />
          <ListItemText primary="Select All" />
        </MenuItem>

        {/* Folder Options */}
        {folders.map((folder) => (
          <MenuItem key={folder.id} value={folder.id}>
            <Checkbox
              checked={selectedFoldersForExport.some((f) => f.id === folder.id)}
              sx={{
                color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9",
                "&.Mui-checked": { color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" },
              }}
            />
            <ListItemText primary={folder.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* Custom Filename Input */}
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          mb: 1,
          color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
          fontSize: "0.95rem",
          fontWeight: "600",
        }}
      >
        Custom Filename (Optional)
      </Typography>
      <TextField
        fullWidth
        placeholder="Enter custom filename (e.g., My_Flashcards_2024)"
        value={customExportFilename}
        onChange={(e) => setCustomExportFilename(e.target.value)}
        helperText="Leave empty to use auto-generated name. Extension will be added automatically."
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: mode === "dark" ? "#222" : mode === "custom" ? customColors.bg : "white",
            "& fieldset": {
              borderColor: mode === "dark" ? "#444" : mode === "custom" ? customColors.border : "#e0e7ff",
            },
            "&:hover fieldset": {
              borderColor: mode === "dark" ? "#666" : mode === "custom" ? customColors.secondary : "#44a6e9",
            },
            "&.Mui-focused fieldset": {
              borderColor: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.primary : "#142be1",
            },
          },
          "& .MuiInputBase-input": {
            color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
          },
          "& .MuiFormHelperText-root": {
            color: mode === "dark" ? "#999" : mode === "custom" ? customColors.textSecondary : "#666",
            fontSize: "0.8rem",
          },
        }}
      />
    </Box>

    {/* Preview of filename */}
    {selectedFoldersForExport.length > 0 && (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "8px",
          backgroundColor: mode === "dark" ? "rgba(68, 166, 233, 0.1)" : mode === "custom" ? `${customColors.secondary}10` : "rgba(20, 43, 225, 0.05)",
          border: `1px dashed ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
            mb: 0.5,
          }}
        >
          Preview filename:
        </Typography>
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: "600",
            color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.primary : "#142be1",
            fontFamily: "monospace",
          }}
        >
          {generatePreviewFilename()}
        </Typography>
      </Box>
    )}
  </DialogContent>

  <DialogActions sx={{ p: 2, backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bg : "#fff" }}>
    <Button
      onClick={() => {
        setOpenDialog(false);
        setSelectedFoldersForExport([]);
        setCustomExportFilename("");
      }}
      sx={{
        color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
        padding: "10px 24px",
        fontSize: "1rem",
        fontWeight: "600",
        borderRadius: "10px",
        textTransform: "none",
        "&:hover": {
          backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.background : "rgba(0,0,0,0.04)",
        },
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleExport}
      disabled={selectedFoldersForExport.length === 0}
      variant="contained"
      sx={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        color: "white",
        padding: "10px 28px",
        fontSize: "1rem",
        fontWeight: "700",
        borderRadius: "10px",
        textTransform: "none",
        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
        "&:hover": {
          background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
          boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
        },
        "&.Mui-disabled": {
          background: "#d1d5db",
          color: "white",
          opacity: 0.6,
        },
      }}
    >
      <BackupIcon sx={{ marginRight: "8px", fontSize: "1.2rem" }} />
      Export
    </Button>
  </DialogActions>
</Dialog> 

<Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)}>
  <DialogTitle
    sx={{
      fontSize: "1.8rem",
      fontWeight: "800",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      paddingBottom: "10px",
      borderBottom: "2px solid #e5e7eb",
      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000",
    }}
  >
    <BackupIcon sx={{ marginRight: "10px", verticalAlign: "middle" }} />
    Export Template
  </DialogTitle>
  <DialogContent sx={{ py: 3, backgroundColor: mode === "dark" ? "#121212" : mode === "custom" ? customColors.bg : "#fff" }}>
    <Typography
      sx={{
        textAlign: "center",
        color: mode === "dark" ? "#ccc" : mode === "custom" ? customColors.textPrimary : "#555",
        mb: 1.5,
        fontSize: "1.05rem",
        fontWeight: 500,
      }}
    >
      Choose the format for the empty template.
    </Typography>

    {/* Export Template Format Selection */}
    <Box sx={{ mb: 2, mt: 2 }}>
      <Typography sx={{ mb: 1, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#000", fontWeight: "600" }}>
        Export Template Format
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormControlLabel
          control={
            <Radio
              checked={exportTemplateFormat === "csv"}
              onChange={() => setExportTemplateFormat("csv")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="CSV"
        />
        <FormControlLabel
          control={
            <Radio
              checked={exportTemplateFormat === "markdown"}
              onChange={() => setExportTemplateFormat("markdown")}
              sx={{ color: mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#44a6e9" }}
            />
          }
          label="Markdown"
        />
      </Box>
    </Box>
  </DialogContent>
  <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 2, backgroundColor: mode === "dark" ? "#1E1E1E" : mode === "custom" ? customColors.bg : "#fff" }}>
    {/* Cancel Button */}
    <Button
      onClick={() => setOpenTemplateDialog(false)}
      sx={{
        color: mode === "dark" ? "#aaa" : mode === "custom" ? customColors.textSecondary : "#666",
        padding: "10px 24px",
        fontSize: "1rem",
        fontWeight: "600",
        borderRadius: "10px",
        textTransform: "none",
        "&:hover": {
          backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.background : "rgba(0,0,0,0.04)",
        },
      }}
    >
      Cancel
    </Button>

    {/* Export Template Button */}
    <Button
      onClick={handleExportTemplate}
      variant="contained"
      sx={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
        color: "white",
        padding: "10px 28px",
        fontSize: "1rem",
        fontWeight: "700",
        borderRadius: "10px",
        textTransform: "none",
        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
        "&:hover": {
          background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
          boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
        },
      }}
    >
      <BackupIcon sx={{ marginRight: "8px", fontSize: "1.2rem" }} />
      Export Template ({exportTemplateFormat === "csv" ? "CSV" : "Markdown"})
    </Button>
  </DialogActions>
</Dialog>

  {/* </DialogActions>
</Dialog> */}

      <Dialog open={createModalOpen} onClose={handleCreateModalClose}>
        <DialogTitle
          sx={{
            background: mode === 'dark' ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === 'custom' ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)',
            color: 'white',
            textAlign: 'center',
            py: 2,
            fontWeight: 'bold'
          }}>Create Profile</DialogTitle>
        <DialogContent sx={{ backgroundColor: mode === "dark" ? '#121212' : mode === "custom" ? customColors.bg : '#fff', py: 3, px: 2 }}>
          <DialogContentText sx={{ textAlign: 'center', color: mode === "dark" ? '#fff' : mode === "custom" ? customColors.text : '#666' }}>
            You have not created a profile yet. Please create your profile to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, backgroundColor: mode === "dark" ? '#121212' : mode === "custom" ? customColors.bg : '#fff' }}>
          {/* Cancel Button */}
          <Button
            onClick={() => {
              handleCreateModalClose();   // close Create Profile dialog
              handleProfileModalClose();  // close Profile modal & stop editing
            }}
            sx={{
              color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.primary : "#142be1",
              borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#142be1",
              "&:hover": {
                borderColor: mode === "dark" ? "#ccc" : mode === "custom" ? customColors.text : "#0d1abf",
                backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.background : "rgba(20,43,225,0.08)"
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>

          {/* Create Profile Button */}
          <Button
            onClick={() => {
              handleCreateModalClose();   // close Create Profile dialog
              setIsProfileEditing(true);  // open profile form for editing/creating
            }}
            sx={{
              backgroundColor: mode === "dark" ? "#142be1" : mode === "custom" ? customColors.primary : "#1976d2",
              "&:hover": {
                backgroundColor: mode === "dark" ? "#0d1abf" : mode === "custom" ? customColors.background : "#1565c0"
              }
            }}
            variant="contained"
          >
            Create Profile
          </Button>
        </DialogActions>

      </Dialog>

      {/* Add required fields modal */}
      <Dialog open={requiredOpen} onClose={handleRequiredClose}>
        <DialogTitle sx={{
          background: mode === 'dark' ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === 'custom' ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)',
          color: 'white',
          textAlign: 'center',
          py: 2,
          fontWeight: 'bold'
        }}>Missing Information</DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', color: mode === "dark" ? '#fff' : mode === "custom" ? customColors.text : '#666' }}>
            Please complete the missing fields to save your profile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleRequiredClose} variant="contained"
            sx={{
              backgroundColor: mode === 'dark' ? '#142be1' : mode === 'custom' ? customColors.primary : '#44a6e9',
              '&:hover': { backgroundColor: mode === 'dark' ? '#0d1abf' : mode === 'custom' ? customColors.background : '#0d1abf' },
              px: 4,
              py: 1,
              borderRadius: 1
            }} color="primary">OK</Button>
        </DialogActions>
      </Dialog>

      {/* Add error modal for API failures */}
      <Dialog open={errorModal.open} onClose={handleErrorModalClose}>
        <DialogTitle sx={{
          background: mode === "dark" ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === "custom" ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)',
          color: 'white',
          textAlign: 'center',
          py: 2,
          fontWeight: 'bold'
        }}>
          Error
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 2, backgroundColor: mode === "dark" ? '#121212' : mode === "custom" ? customColors.bg : '#fff' }}>
          <Typography sx={{ textAlign: 'center', color: mode === "dark" ? '#fff' : mode === "custom" ? customColors.text : '#666' }}>
            Something went wrong. Please try again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 2, backgroundColor: mode === "dark" ? '#121212' : mode === "custom" ? customColors.bg : '#fff' }}>
          <Button
            onClick={handleErrorModalClose}
            variant="contained"
            sx={{
              backgroundColor: mode === "dark" ? '#142be1' : mode === "custom" ? customColors.bgNav : '#44a6e9',
              '&:hover': { backgroundColor: mode === "dark" ? '#0d1abf' : mode === "custom" ? customColors.background : '#1565c0' },
              px: 4,
              py: 1,
              borderRadius: 1
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* 🌈 Custom Theme Modal */}
     <Modal
  open={openThemeModal}
  onClose={() => setOpenThemeModal(false)}
  aria-labelledby="custom-theme-modal"
  sx={{
    backdropFilter: "blur(4px)",
  }}
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }}
  >
    <CustomThemePicker onClose={() => setOpenThemeModal(false)} />
  </Box>
</Modal>
    </Box>
    // </DndProvider>
  );
}
